import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CaseFilter, Repositories } from '@core/db';
import type { Case, CaseEvent, CaseStatus, CaseType, Client, Tag } from '@core/domain/model';
import { readOneOf, writePreference } from '@core/settings/preferences';
import { toListItem } from './formatter';
import { sortCases, type CaseContext } from './sorting';
import type { CasesFilter, CasesSort, CasesState } from './types';
import { telemetry } from '@core/telemetry';

const SORT_KEY = 'cases.sort';
const SORTS = ['urgency', 'recent', 'title', 'manual'] as const;

/** Фильтр экрана → фильтр репозитория. «Все» показывает дела в работе ([D-38]). */
function toQuery(filter: CasesFilter): CaseFilter {
  const statuses: Partial<Record<CasesFilter, CaseStatus[]>> = {
    all: ['Active', 'Suspended'],
    active: ['Active'],
    suspended: ['Suspended'],
    completed: ['Completed'],
  };
  const types: Partial<Record<CasesFilter, CaseType[]>> = {
    consultation: ['Consultation'],
    claim: ['ClaimWork'],
    court: ['CourtCase'],
    enforcement: ['Enforcement'],
    other: ['Other'],
  };

  // У фильтров по типу состояние тоже «в работе»: завершённое дело не должно
  // всплывать в «Судебных» — оно открывается своим фильтром.
  return {
    statuses: statuses[filter] ?? ['Active', 'Suspended'],
    types: types[filter] ?? [],
  };
}

/**
 * Данные экрана «Дела».
 *
 * Роль вьюмодели: экран получает готовое состояние и вызывает действия, ничего
 * не зная ни про базу, ни про сортировку. Так его можно отрисовать и проверить
 * без хранилища — та же граница, что у `XxxRoute` и `XxxScreen` на Android.
 */
export function useCases(db: Repositories, now: () => number = Date.now) {
  const [filter, setFilter] = useState<CasesFilter>('all');
  const [sort, setSortValue] = useState<CasesSort>(() =>
    readOneOf(SORT_KEY, SORTS, 'urgency'),
  );
  const [clientFilterId, setClientFilterId] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isReordering, setReordering] = useState(false);

  const [cases, setCases] = useState<Case[]>([]);
  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [tagsByCase, setTagsByCase] = useState<Map<string, Tag[]>>(new Map());
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  /** Перечитать всё: после любой правки список обязан показать новое состояние. */
  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const query = { ...toQuery(filter), clientId: clientFilterId, tagIds: selectedTags };

      const [loadedCases, loadedEvents, loadedClients, byCase, tags] = await Promise.all([
        db.cases.list(query),
        db.events.all(),
        db.clients.list(),
        db.tags.byCase(),
        db.tags.all(),
      ]);

      if (dropped) return;

      setCases(loadedCases);
      setEvents(loadedEvents);
      setClients(loadedClients);
      setTagsByCase(byCase);
      setAllTags(tags);
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, filter, clientFilterId, selectedTags, version]);

  const state: CasesState = useMemo(() => {
    const moment = now();
    const clientsById = new Map(clients.map((client) => [client.id, client]));

    const contexts: CaseContext[] = cases.map((item) => {
      const own = events.filter((event) => event.caseId === item.id && !event.isCompleted);
      const at = (event: CaseEvent) => event.deadlineAt ?? event.startAt;

      // Просроченное — самое давнее: оно опаснее. Ближайшее — самое раннее из будущих.
      const overdue = own
        .filter((event) => at(event) < moment)
        .sort((a, b) => at(a) - at(b))[0];
      const upcoming = own
        .filter((event) => at(event) >= moment)
        .sort((a, b) => at(a) - at(b))[0];

      return { item, overdue: overdue ?? null, upcoming: upcoming ?? null };
    });

    return {
      isLoading,
      filter,
      sort,
      cases: sortCases(contexts, sort).map((context) =>
        toListItem(
          context,
          context.item.clientId ? (clientsById.get(context.item.clientId) ?? null) : null,
          tagsByCase.get(context.item.id) ?? [],
        ),
      ),
      tagFilters: allTags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        isSelected: selectedTags.includes(tag.id),
      })),
      clients: clients.map((client) => ({ id: client.id, name: client.fullName })),
      clientFilterId,
      isReordering,
    };
  }, [
    cases,
    events,
    clients,
    tagsByCase,
    allTags,
    selectedTags,
    filter,
    sort,
    clientFilterId,
    isLoading,
    isReordering,
    now,
  ]);

  /**
   * Выбор «Своего порядка» сразу включает перетаскивание — [D-40](docs/DECISIONS.md):
   * иначе выбор сортировки не делает ничего видимого, и «как их перетаскивать?» —
   * законный вопрос. При запуске режим не включается, даже если порядок сохранён.
   */
  const setSort = useCallback((next: CasesSort) => {
    setSortValue(next);
    writePreference(SORT_KEY, next);
    setReordering(next === 'manual');
  }, []);

  const actions = useMemo(
    () => ({
      setFilter,
      setSort,
      setClientFilter: setClientFilterId,
      toggleTag: (id: string) =>
        setSelectedTags((selected) =>
          selected.includes(id) ? selected.filter((tag) => tag !== id) : [...selected, id],
        ),
      finishReordering: () => setReordering(false),

      /**
       * Заводит клиента, не уходя из создания дела.
       *
       * Дело заводят на клиента, которого в приложении ещё нет, — и отправлять юриста
       * за этим в другой раздел значит потерять дело: он уходит и не возвращается.
       * Возвращается идентификатор, чтобы клиент сразу встал в это дело.
       */
      async createClient(draft: {
        fullName: string;
        phone: string | null;
        email: string | null;
      }): Promise<string | null> {
        const fullName = draft.fullName.trim();
        if (fullName.length === 0) return null;

        const created = await db.clients.create({
          id: '',
          fullName,
          phone: draft.phone?.trim() || null,
          email: draft.email?.trim() || null,
          birthDate: null,
          address: null,
          notes: null,
        });
        if (created.ok) telemetry.record('clientCreated');
        refresh();

        return created.ok ? created.value : null;
      },

      /** Заводит дело. Обязательное поле одно — название («Структура» §12). */
      async createCase(draft: {
        title: string;
        type: CaseType;
        typeOther: string | null;
        clientId: string | null;
      }) {
        const created = await db.cases.create({
          id: '',
          title: draft.title,
          clientId: draft.clientId,
          type: draft.type,
          status: 'Active',
          typeOther: draft.typeOther,
          description: null,
          notes: null,
          links: [],
          isPinned: false,
          sortOrder: 0,
        });
        if (created.ok) telemetry.record('caseCreated');
        refresh();

        return created;
      },

      async togglePinned(id: string, isPinned: boolean) {
        await db.cases.setPinned(id, isPinned);
        refresh();
      },

      async reorder(orderedIds: string[]) {
        await db.cases.reorder(orderedIds);
        refresh();
      },

      /** Срок ставится задачей с датой: то же, что «Поставить срок» на Android. */
      async setDeadline(caseId: string, title: string, deadlineAt: number) {
        await db.events.create({
          id: '',
          title,
          type: 'Task',
          startAt: deadlineAt,
          endAt: null,
          description: null,
          caseId,
          clientId: null,
          reminderMinutesBefore: null,
          isCompleted: false,
          deadlineAt,
        });
        // Срок — это задача, и считается он задачей: на Android то же самое действие
        // и тот же счётчик, иначе цифры двух версий нельзя сложить.
        telemetry.record('taskCreated');
        refresh();
      },

      refresh,
    }),
    [db, refresh, setSort],
  );

  return { state, actions };
}
